/**
 * Table component for displaying machines
 */
import React from 'react';
import { Table, Space, Button, Popconfirm, Tag, Tooltip, message } from 'antd';
import {
  DownloadOutlined,
  CloudUploadOutlined,
  FileTextOutlined,
  MailOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Machine } from '../types';
import { machineApi } from '../api';

interface MachineTableProps {
  machines: Machine[];
  loading: boolean;
  onEdit: (machine: Machine) => void;
  onDelete: (id: number) => void;
  onEmailPreview: (machine: Machine) => void;
  onRefresh: () => void;
}

const MachineTable: React.FC<MachineTableProps> = ({
  machines,
  loading,
  onEdit,
  onDelete,
  onEmailPreview,
  onRefresh,
}) => {
  const handleDownload = (machine: Machine) => {
    const token = localStorage.getItem('token');
    const url = machineApi.downloadUrl(machine.id);
    // Open in new tab with auth header via form submission
    const form = document.createElement('form');
    form.method = 'GET';
    form.action = url;
    form.target = '_blank';

    // For token auth, we need to use fetch and create a blob
    fetch(url, {
      headers: {
        'Authorization': `Token ${token}`,
      },
    })
      .then(response => {
        if (!response.ok) throw new Error('Download failed');
        return response.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `license_${machine.customer_company_name.replace(/\s+/g, '_')}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        message.success('License downloaded successfully');
      })
      .catch(() => {
        message.error('Failed to download license');
      });
  };

  const handleLicenseDownload = (machine: Machine) => {
    const token = localStorage.getItem('token');
    const url = machineApi.licenseUrl(machine.id);

    fetch(url, {
      headers: {
        'Authorization': `Token ${token}`,
      },
    })
      .then(response => {
        if (!response.ok) throw new Error('Download failed');
        return response.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'gudu_sqlflow_license.txt';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        message.success('License file downloaded');
      })
      .catch(() => {
        message.error('Failed to download license file');
      });
  };

  const handleUpload = async (machine: Machine) => {
    try {
      const result = await machineApi.upload(machine.id);
      if (result.success) {
        message.success(result.message);
      } else {
        message.error(result.message);
      }
    } catch {
      message.error('Failed to upload license');
    }
  };

  const handleCopyToken = (token: string) => {
    navigator.clipboard.writeText(token);
    message.success('Token copied to clipboard');
  };

  const columns: ColumnsType<Machine> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Customer',
      dataIndex: 'customer_company_name',
      key: 'customer_company_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Email',
      dataIndex: 'customer_contact_email',
      key: 'customer_contact_email',
      width: 180,
      ellipsis: true,
      render: (email: string) => (
        <Tooltip title={email}>
          <a href={`mailto:${email}`}>{email}</a>
        </Tooltip>
      ),
    },
    {
      title: 'Version',
      dataIndex: 'product_version',
      key: 'product_version',
      width: 80,
    },
    {
      title: 'Databases',
      dataIndex: 'supported_databases',
      key: 'supported_databases',
      render: (text: string) => {
        if (!text) return '-';
        const dbs = text.split(',').map(db => db.trim()).filter(Boolean);
        return (
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {dbs.map(db => (
              <Tag key={db} style={{ marginRight: 0 }}>{db}</Tag>
            ))}
          </span>
        );
      },
    },
    {
      title: 'Purchase Date',
      dataIndex: 'purchase_date',
      key: 'purchase_date',
      width: 110,
    },
    {
      title: 'Days',
      dataIndex: 'available_days',
      key: 'available_days',
      width: 60,
    },
    {
      title: 'Type',
      dataIndex: 'license_type',
      key: 'license_type',
      width: 100,
      render: (type: string) => (
        <Tag color={type === 'enterprise' ? 'blue' : 'green'}>
          {type === 'enterprise' ? 'Enterprise' : 'Machine'}
        </Tag>
      ),
    },
    {
      title: 'Token',
      dataIndex: 'token',
      key: 'token',
      width: 100,
      render: (token: string) => (
        token ? (
          <Tooltip title="Click to copy">
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopyToken(token)}
            >
              Copy
            </Button>
          </Tooltip>
        ) : '-'
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 280,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Download ZIP">
            <Button
              type="primary"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
          <Tooltip title="Upload to gudusoft.com">
            <Popconfirm
              title="Upload license to gudusoft.com?"
              onConfirm={() => handleUpload(record)}
            >
              <Button
                size="small"
                icon={<CloudUploadOutlined />}
              />
            </Popconfirm>
          </Tooltip>
          <Tooltip title="Download License File">
            <Button
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => handleLicenseDownload(record)}
            />
          </Tooltip>
          <Tooltip title="Email Preview">
            <Button
              size="small"
              icon={<MailOutlined />}
              onClick={() => onEmailPreview(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Delete this machine?"
              description="This action cannot be undone."
              onConfirm={() => onDelete(record.id)}
            >
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={machines}
      rowKey="id"
      loading={loading}
      size="middle"
      pagination={{
        pageSize: 20,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} machines`,
      }}
      scroll={{ x: 1400 }}
    />
  );
};

export default MachineTable;
